import React, { useState, useEffect } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Typography,
  Box,
  Autocomplete,
  TextField,
  Grid,
} from '@mui/material';
import { useParams } from 'react-router-dom';

// CONFIG
import Config from '../../Config';

// COMPONENTS
import CustomerTableRow from '../../components/admin/customers/CustomerTableRow';
import CustomerAdd from '../../components/admin/customers/CustomerAdd';
import Loader from '../../components/common/Loader';
import Layout from '../../components/layouts/Layout';

// I18N
import CustomersFormattedMessages from '../../messages/admin/CustomersFormattedMessages';

// NETWORK
import useAdminRequest from '../../requests/AdminRequest';
import useProvidersRequest from '../../requests/ProviderRequest';
// ROUTES
import { AdminRoutes } from '../../routes/AdminRoutes';

const Customers = (props) => {
  /*
    Expected Props:
      - customers: An array of Customer objects
      - customerId: (Optional) If present, load single Customer directly
      - action: An action to take against the Customer. Values include 'payment'
  */

  const { type, id } = useParams();

  const [customers, setCustomers] = useState([]);
  const [showAddCustomer, setShowAddCustomer] = useState(false);
  const [showPaymentMethodPage, setShowPaymentMethodPage] = useState(false);
  const [loading, setLoading] = useState(true);
  const [providers, setProviders] = useState([]);
  const [providerFilter, setProviderFilter] = useState(null);
  const [error, setError] = useState(null);

  const { getAllCustomers } = useAdminRequest();
  const { getAllProviders } = useProvidersRequest();

  // Load Payment section if action is present in props //TODO: Delete payment section
  if (type === 'payment') {
    setShowPaymentMethodPage(true);
  }

  useEffect(() => {
    async function getData() {
      if (props.customer && props.customer !== null) {
        const customers = await getCustomers(props.errorHandler);

        if (type === 'payment' && id) {
          setShowPaymentMethodPage(true);
          setLoading(false);
        } else if (customers) {
          if (props.location.state) {
            try {
              setCustomers(customers);
              setLoading(true);
            } catch (e) {
              setCustomers(customers);
              setLoading(false);
            }
          } else {
            setCustomers(customers);
            setLoading(false);
          }
        }

        await getProviders();
      }
    }

    getData();
  }, [props, type, id]);

  // Updates when the provider filter is updated
  useEffect(() => {
    async function getFilteredData() {
      setLoading(true);
      const customers = await getCustomers();
      setCustomers(customers);
      setLoading(false);
    }

    getFilteredData();
  }, [providerFilter]);

  /*
    Used to fetch Customer
  */
  const getCustomers = async () => {
    return await getAllCustomers(providerFilter, props.errorHandler);
  };

  const getProviders = async () => {
    const providers = await getAllProviders(props.errorHandler);
    setProviders(providers);
  };

  /*
    Used to display the Add Customer View
  */
  const addNewCustomer = () => {
    setShowAddCustomer(true);
  };

  /*
    Handle back button click from Add/Edit Customer View
    Inputs:
      - to: Where the back button is going back to
  */
  const handleBackButtonClick = async (to) => {
    const { customerId } = props;
    let url = '/admin/customers';

    // Going back to Customer Single View & have Id in url
    if (to && to.toLowerCase() === 'customers' && customerId) {
      url += `/${customerId}`;
      window.history.pushState(
        { tab: 'customers' },
        'Customer: ' + customerId,
        url
      );
    }
    // Else if returning to the Customer List
    else if (!to || to.toLowerCase() === 'list') {
      window.history.pushState({ tab: 'customers' }, 'Customer List', url);
      props.navigate(url);
    }
    // Refresh the Customer list (eg. after creating new Customer)
    else if (to && to.toLowerCase() === 'refresh') {
      const customers = await getCustomers(props.errorHandler);
      setCustomers(customers);
      window.history.pushState({ tab: 'customers' }, 'Customer List', url);
    }

    setShowAddCustomer(false);
    setShowPaymentMethodPage(false);
  };

  /*
    Main Content generating function
  */
  const generateContent = () => {
    // Handle error state
    if (error) {
      return <Typography>{error}</Typography>;
    }

    // Display Single Customer view for adding a new Customer
    if (showAddCustomer) {
      return (
        <CustomerAdd
          backButtonClick={handleBackButtonClick}
          errorHandler={props.errorHandler}
        />
      );
    }

    // Default state - List View
    return generateTable();
  };

  /*
    Generates whole table for Customers (headers and rows)
  */
  const generateTable = () => {
    return (
      <>
        <Grid container spacing={2}>
          <Grid size={12}>
            <Autocomplete
              options={providers}
              getOptionLabel={(option) => option.provider_name}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Filter by Provider"
                  size="small"
                />
              )}
              onChange={(_, newValue) => {
                setProviderFilter(newValue?.provider_id);
              }}
              sx={{ width: 480, mb: 2 }}
            />
          </Grid>
        </Grid>
        <TableContainer
          sx={{ marginBottom: 4, overflowX: 'auto', width: '100%' }}
        >
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{CustomersFormattedMessages.nameText}</TableCell>
                <TableCell>
                  {CustomersFormattedMessages.typeClassStatus}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>{sortListForTable()}</TableBody>
          </Table>
        </TableContainer>
      </>
    );
  };

  const sortListForTable = () => {
    const sortedCustomers = customers.sort((a, b) =>
      a.customer_name.localeCompare(b.customer_name)
    );

    return generateTableBody(sortedCustomers);
  };

  /*
    Generate Table Body
  */
  const generateTableBody = (customers) => {
    return customers.map((customer, index) => (
      <CustomerTableRow
        key={`${customer.customer_id}-${index}`}
        id={customer.customer_id}
        name={customer.customer_name}
        type={customer.customer_type_name || ''}
        cust_class={customer.customer_class_name || ''}
        active={customer.customer_status_name || ''}
        customer={customer}
        onClickName={() =>
          props.navigate(`/admin/customers/${customer.customer_id}`)
        }
      />
    ));
  };

  // render return
  let { accessLevel, accessType } = props;

  if (
    accessLevel === Config.accessLevel.admin &&
    accessType === Config.accessType.roxAdmin
  ) {
    return (
      <Layout
        title={
          showAddCustomer
            ? 'Add Customer'
            : CustomersFormattedMessages.listOfCustomers
        }
        routes={AdminRoutes}
        accessLevel={accessLevel}
        accessType={accessType}
        backButtonText={showAddCustomer && 'Back'}
        handleBackButton={() => handleBackButtonClick('list')}
        titleRowRightComponent={
          !showAddCustomer && (
            <Box>
              <Button
                onClick={() => addNewCustomer()}
                variant="contained"
                color="secondary"
              >
                {CustomersFormattedMessages.addNewText}
              </Button>
            </Box>
          )
        }
      >
        {loading ? <Loader /> : generateContent()}
      </Layout>
    );
  }
};

export default Customers;
